import { useState } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";

import { DownloadContext } from "./DownloadContext";
import GetMeasurements from "../../repositories/Measurements/GetMeasurements";
import GetPatientByPatientId from "../../repositories/Patients/GetPatientByPatientId";
import { FormatDateForFileName } from "../../../utilities/FormatDate";
import ConvertMeasurementsToString from "../../../utilities/ConvertMeasurementsToString";
import LocalStorage from "../../../utilities/Storage";

const KEEP_ALIVE_INTERVAL_MS = 30000;

const initialProgress = {
    feedingsCompleted: 0,
    feedingsTotal: 0,
    patientsCompleted: 0,
    patientsTotal: 0,
    currentPatient: '',
};

export const DownloadProvider = ({ children }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [progress, setProgress] = useState(initialProgress);
    const [error, setError] = useState(null);

    const startDownload = async (facility, patients) => {
        if (isDownloading) return;

        setIsDownloading(true);
        setError(null);

        const totalFeedings = patients.reduce((sum, p) => sum + (parseInt(p.total_feedings) || 0), 0);
        setProgress({ ...initialProgress, patientsTotal: patients.length, feedingsTotal: totalFeedings });

        // Refresh lastInteractionTime periodically so the idle timeout
        // does not log the user out during a long download.
        const keepAliveTimer = setInterval(() => {
            LocalStorage.setItem('lastInteractionTime', Date.now());
        }, KEEP_ALIVE_INTERVAL_MS);

        try {
            const zip = new JSZip();

            for (let pi = 0; pi < patients.length; pi++) {
                const patient = await GetPatientByPatientId(patients[pi].patient_id);

                setProgress(prev => ({
                    ...prev,
                    patientsCompleted: pi,
                    currentPatient: patient.name,
                }));

                for (const feeding of patient.feedings) {
                    const measurements = await GetMeasurements(feeding.timestamp, patient.patient_id);
                    const csvString = ConvertMeasurementsToString(feeding, measurements);

                    const fileName =
                        `${patient.ehr_patient_ref}_${patient.name.replace(", ", "-").trim()}_` +
                        `${FormatDateForFileName(feeding.timestamp)}.csv`;

                    zip.file(fileName, csvString);

                    setProgress(prev => ({ ...prev, feedingsCompleted: prev.feedingsCompleted + 1 }));
                }

                setProgress(prev => ({ ...prev, patientsCompleted: pi + 1 }));
            }

            const content = await zip.generateAsync({ type: "blob" });
            saveAs(content, `${facility.name}_${FormatDateForFileName(Date.now() / 1000)}_feeding_data.zip`);
        } catch (err) {
            setError('Download failed. Please try again.');
            console.error('Download error:', err);
        } finally {
            clearInterval(keepAliveTimer);
            setIsDownloading(false);
            setProgress(initialProgress);
        }
    };

    return (
        <DownloadContext.Provider value={{ isDownloading, progress, error, startDownload }}>
            {children}
        </DownloadContext.Provider>
    );
};
